import React, { useEffect, useRef } from "react";
import {
  cn,
  getBackgroundStyles,
  getBorderStyles,
  getDisabledStyles,
  getFocusRingStyles,
  getForegroundStyles,
} from "../../styles";
import { mdiClose } from "@mdi/js";
import { LocalizationScope } from "../../util/LocalizationScope";
import { Button, ButtonVariant, Typography } from "../../atoms";
import { PanelColor, ThemeColor } from "../../props";
import { processErrorContent } from "./helper";
import { BaseFormFieldProps, FormFieldConstraint } from "./index";

export interface FormFieldTextAreaProps extends BaseFormFieldProps {
  /**
   * Validation constraints for the field.
   */
  constraints: FormFieldConstraint;
  /**
   * Current value of the textarea field.
   */
  value: string | null;
  /**
   * Callback function called when the field value changes.
   */
  onChangeField: (id: string, value: string | null) => void;
  /**
   * Placeholder text shown when the field is empty.
   */
  placeholder?: string;
  /**
   * Automatically focuses the field when mounted.
   * @default false
   */
  autoFocus?: boolean;
  /**
   * Callback function called when Ctrl+Enter is pressed.
   * Enables quick form submission.
   */
  onQuickSubmit?: () => void;
  /**
   * Automatically selects all text when the field receives focus.
   * @default false
   */
  autoSelectAll?: boolean;
  /**
   * Custom element to display at the end of the textarea.
   * Replaces the default clear button when provided.
   */
  endAdornment?: React.ReactNode;
  /**
   * Shows a clear button when the field has a value and is not required.
   * @default true
   */
  clearable?: boolean;
  /**
   * Applies bold font weight to the textarea text.
   * @default false
   */
  strong?: boolean;
  /**
   * Number of visible text rows.
   * @default 4
   */
  rows?: number;
  /**
   * Controls the resize behavior of the textarea.
   * @default "vertical"
   */
  resize?: "none" | "vertical" | "horizontal" | "both";
  /**
   * Background color theme for the textarea.
   * @default PanelColor.level0
   */
  color?: PanelColor;
}

export const TextArea: React.FC<FormFieldTextAreaProps> = ({
  className,
  id,
  value,
  onChangeField,
  label = "Label",
  localKey,
  errors = [],
  touched = false,
  helper = null,
  helperKey,
  constraints,
  placeholder,
  autoFocus,
  disabled,
  onQuickSubmit,
  autoSelectAll,
  keepHeight,
  noLabel,
  endAdornment,
  clearable = true,
  strong,
  dense,
  rows = 4,
  resize = "vertical",
  color = PanelColor.level0,
  readOnly,
}) => {
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const showClear = !disabled && !readOnly && clearable && !constraints.presence && !!value;
  const error = touched && errors[0];

  const handleQuickSubmit = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (
      ((e.key === "Enter" && e.ctrlKey) || (e.charCode === 13 && e.ctrlKey)) &&
      onQuickSubmit
    ) {
      onQuickSubmit();
    }
  };

  const handleFocus = (e: React.FocusEvent<HTMLTextAreaElement>) => {
    e.target.select();
  };

  useEffect(() => {
    if (!disabled && autoFocus && textareaRef.current)
      textareaRef.current.focus();
  }, [disabled, autoFocus]);

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) =>
    onChangeField(id, e.target.value);
  const handleClear = () => onChangeField(id, null);

  return (
    <div className={className}>
      {!noLabel && (
        <div className="flex">
          <Typography.Label
            htmlFor={id}
            className={cn(
              "block font-medium select-none",
              dense ? "mb-0.5 text-xs" : "mb-1 text-sm",
            )}
            content={label || { local: id }}
          />
          {!!constraints.presence && (
            <span className="text-red-500 ml-0.5 text-xs align-super">*</span>
          )}
        </div>
      )}
      <div className="flex gap-2 items-start">
        <div className="grow relative">
          <textarea
            ref={textareaRef}
            id={id}
            autoFocus={autoFocus}
            autoComplete="off"
            required={!!constraints.presence}
            placeholder={placeholder}
            rows={rows}
            value={value || ""}
            onChange={handleChange}
            onKeyDown={onQuickSubmit ? handleQuickSubmit : undefined}
            onFocus={autoSelectAll ? handleFocus : undefined}
            disabled={disabled}
            readOnly={readOnly}
            className={cn(
              "w-full px-3 rounded-md border min-h-[38px]",
              dense ? "py-1.5 text-xs" : "py-2 text-sm",
              dense ? "rounded" : "rounded-md",
              "focus:outline-none focus:ring-2",
              error
                ? getFocusRingStyles(ThemeColor.error)
                : getFocusRingStyles(ThemeColor.primary),
              getBackgroundStyles(color),
              getBorderStyles(error ? ThemeColor.error : ThemeColor.gray),
              getForegroundStyles(color),
              "placeholder:text-current placeholder:opacity-50",
              strong && "font-medium",
              disabled && getDisabledStyles(),
              resize === "none" && "resize-none",
              resize === "vertical" && "resize-y",
              resize === "horizontal" && "resize-x",
              resize === "both" && "resize",
            )}
            aria-invalid={!!error}
            aria-describedby={
              helper ? `${id}-helper` : error ? `${id}-error` : undefined
            }
          />
        </div>
        {endAdornment
          ? endAdornment
          : showClear && (
              <Button
                variant={ButtonVariant.text}
                color={ThemeColor.gray}
                onClick={handleClear}
                iconPath={mdiClose}
                dense
                muted
                tabIndex={-1}
                disabled={disabled}
              />
            )}
      </div>
      {(helperKey || helper || keepHeight) && !error && (
        <LocalizationScope scope={localKey}>
          <Typography.P
            id={`${id}-helper`}
            className={cn("text-xs", dense ? "my-0.5" : "my-1")}
            color={ThemeColor.gray}
            content={helperKey ? { local: helperKey } : helper ? helper : null}
          >
            {" "}
          </Typography.P>
        </LocalizationScope>
      )}
      {error && (
        <LocalizationScope scope={[localKey, "::PSS"]}>
          <Typography.P
            id={`${id}-error`}
            className={cn("text-xs", dense ? "my-0.5" : "my-1")}
            color={ThemeColor.error}
            content={processErrorContent(error)}
          />
        </LocalizationScope>
      )}
    </div>
  );
};
